import * as Sentry from "@sentry/vue";
import store from "@/store";
import { ErrorToSentry, SentryErrorContext, SentryReplayConfig, SentryReplayConfigFlag } from "./interfaces/sentryInterfaces";
import {
  isDevelopmentEnv,
  getErrorMessage,
  DEV_SENTRY_SAMPLE_RATE,
  PROD_SENTRY_SAMPLE_RATE,
  DEFAULT_REPLAY_SESSION_SAMPLE_RATE,
  DEFAULT_REPLAY_ERROR_SAMPLE_RATE,
  DEFAULT_NETWORK_REQUEST_HEADERS,
  DEFAULT_NETWORK_RESPONSE_HEADERS
} from "@/utils";
import { App } from "vue";
import { SENTRY_REPLAYS, SENTRY_REPLAY_CONFIG } from "@appetize/iw-common/utils/featureFlags";

declare const IW_VERSION: string;

const getContextData = (): SentryErrorContext => {
  // Simplify the items data to reduce the payload size.
  const rawCartItems = store.getters["cart/getCartItems"] || [];
  const cartItems = JSON.stringify(
    rawCartItems.map(({ item: { id, name }, quantity }) => {
      return { item: { id, name }, quantity };
    })
  );

  return {
    APIType: store.getters["venueSettings/APIType"],
    gatewayId: store.getters["vendor/tokenizedGatewayId"],
    balance: store.getters["cart/getAppliedAmenities"]?.balance || 0,
    venueId: store.getters["venue/getActiveVenue"] || 0,
    vendorId: store.getters["vendor/getActiveVendor"]?.id || 0,
    iwVersion: store.getters["config/getReleaseVersion"],
    cartItems
  };
};

const init = (app: App) => {
  Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    integrations: [new Sentry.BrowserTracing()],
    replaysSessionSampleRate: DEFAULT_REPLAY_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: DEFAULT_REPLAY_ERROR_SAMPLE_RATE,
    tracesSampleRate: isDevelopmentEnv() ? DEV_SENTRY_SAMPLE_RATE : PROD_SENTRY_SAMPLE_RATE,
    logErrors: true,
    environment: store.getters["config/getAppEnvironment"],
    release: `interact-web@${IW_VERSION}`
  });

  // This will be set globally for every subsequent event send
  Sentry.configureScope(function (scope) {
    scope.addEventProcessor(function (event) {
      // If an error doesn't have context data, add it.
      const levelsWithContext = ["error", "fatal", "warning"];
      if (!event?.extra && levelsWithContext.includes(event?.level || "")) {
        event.extra = getContextData();
      }

      return event;
    });
  });
};

const createIssue = (code: string, area: string, func: string, data: object, rawError: object) => {
  const error = getErrorMessage(rawError);
  const contextData = getContextData();
  const errorData: ErrorToSentry = {
    extra: {
      ...contextData,
      area,
      func,
      data,
      error
    }
  };

  Sentry.captureException(new Error(code), errorData);
};

/**
 * Performs Sentry replays initialization, it will only enable replays when the SENTRY_REPLAYS feature flag is on.
 */
const initReplays = () => {
  if (store.getters["featureFlags/getFeatureFlagById"](SENTRY_REPLAYS)) {
    //had to cast client as any or TS complains
    const client = Sentry.getCurrentHub().getClient() as any;
    const replayIntegration = client.getIntegration(Sentry.Replay);

    if (!replayIntegration) {
      if (isDevelopmentEnv())
        console.log(
          "Initializing Sentry replays with the following flag status",
          store.getters["featureFlags/getFeatureFlagById"](SENTRY_REPLAY_CONFIG)
        );

      const sentryReplayConfig: SentryReplayConfigFlag = store.getters["featureFlags/getFeatureFlagById"](SENTRY_REPLAY_CONFIG);
      const options = client?.getOptions();

      //setting SDK level options (not integration level)
      options.replaysSessionSampleRate = sentryReplayConfig?.replaysSessionSampleRate || DEFAULT_REPLAY_SESSION_SAMPLE_RATE;
      options.replaysOnErrorSampleRate = sentryReplayConfig?.replaysOnErrorSampleRate || DEFAULT_REPLAY_ERROR_SAMPLE_RATE;

      /**
       * Only setting options that are changing default configurations
       * Refer to:
       * https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/configuration
       * https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/privacy
       * For default values
       */
      const replayConfig: SentryReplayConfig = {
        networkDetailAllowUrls: [window.location.origin],
        networkRequestHeaders: sentryReplayConfig?.networkRequestHeaders || DEFAULT_NETWORK_REQUEST_HEADERS,
        networkResponseHeaders: sentryReplayConfig?.networkResponseHeaders || DEFAULT_NETWORK_RESPONSE_HEADERS,
        maskAllText: false,
        blockAllMedia: false
      };

      const replay = new Sentry.Replay(replayConfig);
      client.addIntegration(replay);
    }
  }
};

export default {
  init,
  createIssue,
  initReplays
};
